<template>
  <teleport to="body">
    <transition
      name="slide-fade"
      @enter="onEnter"
      @after-enter="onAfterEnter"
      @leave="onLeave"
      @after-leave="onAfterLeave"
      :css="false"
    >
      <div
        v-if="display"
        class="fade-away-message-container"
        :class="{
          [type]: true,
          [displayType]: displayType !== 'default',
        }"
        :style="{
          width: widthStyle,
        }"
      >
        <!-- Icon Section -->
        <div class="icon-section">
          <div class="icon-background" :style="iconBackgroundStyle">
            <v-icon class="notification-icon" color="white">{{
              mdiIcon
            }}</v-icon>
          </div>
        </div>

        <!-- Content Section -->
        <div class="content-section">
          <div class="message-content">
            <div v-if="header" class="message-header">{{ header }}</div>
            <div v-if="message" class="message-text">{{ message }}</div>
          </div>
        </div>

        <!-- Close Button -->
        <div class="close-section">
          <v-btn
            icon
            size="small"
            variant="text"
            @click="clickClose"
            class="close-btn"
          >
            <v-icon size="20">mdi-close</v-icon>
          </v-btn>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script>
export default {
  name: "FadeAwayMessage",
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    header: {
      type: String,
      default: "",
    },
    message: {
      type: String,
      default: "Notification message",
    },
    timeout: {
      type: [Number, String],
      default: 5000,
    },
    top: {
      type: Number,
      default: 10,
    },
    type: {
      type: String,
      default: "success",
      validator: (value) =>
        ["success", "warning", "error", "info"].includes(value),
    },
    displayType: {
      type: String,
      default: "default",
      validator: (value) =>
        ["default", "minimal", "outlined", "variation2"].includes(value),
    },
    width: {
      type: [Number, String],
      default: 400,
    },
  },

  data() {
    return {
      display: false,
      timeoutId: null,
      typeConfig: {
        success: {
          icon: "mdi-check-circle",
          color: "#4CAF50",
        },
        warning: {
          icon: "mdi-alert",
          color: "#FF9800",
        },
        error: {
          icon: "mdi-alert-circle",
          color: "#F44336",
        },
        info: {
          icon: "mdi-information",
          color: "#2196F3",
        },
      },
    };
  },

  computed: {
    widthStyle() {
      return typeof this.width === "number" ? `${this.width}px` : this.width;
    },

    mdiIcon() {
      return this.typeConfig[this.type]?.icon || this.typeConfig.success.icon;
    },

    iconBackgroundStyle() {
      const color =
        this.typeConfig[this.type]?.color || this.typeConfig.success.color;
      return { backgroundColor: color };
    },
  },

  watch: {
    modelValue: {
      handler(val) {
        if (val) {
          this.showNotification();
        } else {
          this.hideNotification();
        }
      },
      immediate: true,
    },
  },

  methods: {
    showNotification() {
      this.display = true;

      // Auto-hide after timeout
      if (this.timeout > 0) {
        this.timeoutId = setTimeout(() => {
          this.hideNotification();
        }, parseInt(this.timeout));
      }
    },

    hideNotification() {
      this.display = false;
      this.clearTimeout();
      this.$emit("update:modelValue", false);
    },

    clickClose() {
      this.hideNotification();
    },

    clearTimeout() {
      if (this.timeoutId) {
        clearTimeout(this.timeoutId);
        this.timeoutId = null;
      }
    },

    onEnter(el) {
      el.style.transform = "translateX(100%)";
      el.style.opacity = "0";

      el.offsetHeight;

      requestAnimationFrame(() => {
        el.style.transition = "all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1)";
        el.style.transform = "translateX(0)";
        el.style.opacity = "1";
      });
    },

    onAfterEnter(el) {
      el.style.transition = "";
      el.style.transform = "";
      el.style.opacity = "";
    },

    onLeave(el, done) {
      el.style.transition = "all 0.3s cubic-bezier(0.4, 0, 0.6, 1)";
      el.style.transform = "translateX(100%)";
      el.style.opacity = "0";

      setTimeout(done, 300);
    },

    onAfterLeave(el) {
      el.style.transition = "";
      el.style.transform = "";
      el.style.opacity = "";
    },
  },

  beforeUnmount() {
    this.clearTimeout();
  },
};
</script>

<style scoped>
.fade-away-message-container {
  position: fixed;
  top: 20px;
  right: 20px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  display: flex;
  align-items: center;
  min-height: 70px;
  max-width: 500px;
  z-index: 9999;
  border-left: 4px solid transparent;
  overflow: hidden;
}

.fade-away-message-container.success {
  border-left-color: #4caf50;
}

.fade-away-message-container.warning {
  border-left-color: #ff9800;
}

.fade-away-message-container.error {
  border-left-color: #f44336;
}

.fade-away-message-container.info {
  border-left-color: #2196f3;
}

/* Icon Section */
.icon-section {
  padding: 16px;
  display: flex;
  align-items: center;
}

.icon-background {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.notification-icon {
  font-size: 20px !important;
}

/* Content Section */
.content-section {
  flex: 1;
  padding: 16px 8px;
  min-width: 0; /* Prevent flex item from overflowing */
}

.message-content {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.message-header {
  font-weight: 600;
  font-size: 14px;
  color: #1a1a1a;
  line-height: 1.2;
}

.message-text {
  font-size: 13px;
  color: #666;
  line-height: 1.3;
  word-wrap: break-word;
}

/* Close Section */
.close-section {
  padding: 8px 12px;
  display: flex;
  align-items: flex-start;
}

.close-btn {
  opacity: 0.7;
  transition: opacity 0.2s ease;
}

.close-btn:hover {
  opacity: 1;
}

/* Slide Animation */
.slide-fade-enter-active {
  transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.slide-fade-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.6, 1);
}

.slide-fade-enter-from {
  transform: translateX(100%);
  opacity: 0;
}

.slide-fade-leave-to {
  transform: translateX(100%);
  opacity: 0;
}

/* Display Type Variations */
.fade-away-message-container.minimal {
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  border-left: none;
  background: #f8f9fa;
}

.fade-away-message-container.minimal .icon-background {
  background: transparent !important;
}

.fade-away-message-container.minimal .notification-icon {
  color: inherit !important;
}

.fade-away-message-container.outlined {
  background: transparent;
  border: 2px solid;
  border-left-width: 4px;
}

.fade-away-message-container.outlined.success {
  border-color: #4caf50;
  background: rgba(76, 175, 80, 0.05);
}

.fade-away-message-container.outlined.warning {
  border-color: #ff9800;
  background: rgba(255, 152, 0, 0.05);
}

.fade-away-message-container.outlined.error {
  border-color: #f44336;
  background: rgba(244, 67, 54, 0.05);
}

.fade-away-message-container.outlined.info {
  border-color: #2196f3;
  background: rgba(33, 150, 243, 0.05);
}

/* Responsive Design */
@media (max-width: 640px) {
  .fade-away-message-container {
    top: 10px;
    right: 10px;
    left: 10px;
    max-width: none;
    width: auto !important;
  }

  .slide-fade-enter-from,
  .slide-fade-leave-to {
    transform: translateY(-100%);
  }
}
</style>
